import { z } from 'zod';

export const FIELD_KINDS = [
  'text',
  'email',
  'phone',
  'select',
  'textarea',
  'department',
  'file',
] as const;
export type FieldKind = (typeof FIELD_KINDS)[number];

export const MAX_FORM_FIELDS = 10;
export const FIELD_KEY = /^[a-z][a-z0-9_]{0,39}$/;

const field = z
  .object({
    key: z.string().trim().regex(FIELD_KEY, { error: 'chatSettings:validation.fieldKey' }),
    kind: z.enum(FIELD_KINDS),
    label: z
      .string()
      .trim()
      .min(1, { error: 'chatSettings:validation.labelRequired' })
      .max(120, { error: 'chatSettings:validation.max120' }),
    required: z.boolean(),
    options: z.array(z.string().trim().min(1).max(120)).max(50),
  })
  .superRefine((value, ctx) => {
    if (value.kind === 'select' && value.options.length === 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['options'],
        message: 'chatSettings:validation.optionsRequired',
      });
    }
  });

export type FormFieldForm = z.infer<typeof field>;

/** Up to ten fields; a key may appear once (it becomes the visitor attribute's name). */
const fields = z
  .array(field)
  .max(MAX_FORM_FIELDS)
  .superRefine((list, ctx) => {
    const seen = new Set<string>();
    list.forEach((item, index) => {
      const key = item.key.trim();
      if (key && seen.has(key)) {
        ctx.addIssue({
          code: 'custom',
          path: [index, 'key'],
          message: 'chatSettings:validation.fieldKeyTaken',
        });
      }
      seen.add(key);
    });
  });

export const formsSchema = z.object({
  preChat: z.object({ enabled: z.boolean(), fields }),
  offlineForm: z.object({
    enabled: z.boolean(),
    fields,
    confirmationText: z.string().trim().max(300, { error: 'chatSettings:validation.max300' }),
  }),
});

export type FormsForm = z.infer<typeof formsSchema>;
export type FormListName = 'preChat' | 'offlineForm';
